/// <reference types='google.maps' />
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseMapLocation } from '@huge-monorepo/shared/interfaces';
import { BehaviorSubject, Observable, debounceTime } from 'rxjs';
import { MapBounds } from '../../interfaces/MapBounds';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { MaterialModule } from "@huge-monorepo/shared/modules"
// import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { SharedToolsService } from '@huge-monorepo/shared/tools';

interface InfoWindowMarkerContent {
  marker: google.maps.Marker;
  location: BaseMapLocation;
}

@Component({
  selector: 'huge-monorepo-base-map',
  standalone: true,
  // imports: [CommonModule, MaterialModule],
  templateUrl: './base-map.component.html',
  styleUrl: './base-map.component.scss',
})
export class BaseMapComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) gmap: ElementRef;

  /**
   * Locations to be displayed on the map.
   * @type {BaseMapLocation[]}
   */
  @Input({ required: true }) locations: BaseMapLocation[] | null;
  /**
   * Should the map calculate the center of the map based on the locations
   * @type {boolean}
   */
  @Input({ required: true }) calculateCenter: boolean;

  /**
   * Specifies whether to cluster the markers on the map.
   * @type {boolean}
   */
  @Input({ required: false }) clusterMarkers: boolean = false;

  /**
   * Specifies whether to show the network provider legend.
   * @type {boolean}
   */
  @Input({ required: false }) showNetworkProviderLegend: boolean = true;

  /**
   * Specifies whether the tower legend should be shown.
   * @type {boolean}
   */
  @Input({ required: false }) showTowerLegend: boolean = true;

  /**
   * Specifies if the action buttons should go to the Top Right.
   * @type {boolean}
   */
  @Input({ required: false }) updateActionButtons: boolean = true;

  /**
   * Location to be focussed on the map.
   * @type {BaseMapLocation}
   */
  @Input({ required: false }) focusedSimTower: BaseMapLocation | null;

  /**
   * Default Zoom level on the map.
   * @type {number}
   */
  @Input({ required: false }) focusedZoom: number;

  /**
   * Specifies whether to fit the bounds of the map to the locations.
   * @type {boolean}
   * @default false
   */
  @Input({ required: false }) fitBounds: boolean = false;

  /**
   * Emits an event when the bounds of the map change.
   * The event payload is an object of type `MapBounds`.
   * @type {EventEmitter<MapBounds>}
   */
  @Output() boundsChange: EventEmitter<MapBounds> = new EventEmitter<MapBounds>();

  markers: google.maps.Marker[] = [];
  map: google.maps.Map;
  mapOptions: google.maps.MapOptions;
  lat = -26.061351;
  lng = 28.153786;
  mapBounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

  markerClusterer: MarkerClusterer;

  debounce: BehaviorSubject<boolean> = new BehaviorSubject(false);
  debounce$: Observable<any> = this.debounce.asObservable();

  markerMap: Map<number, InfoWindowMarkerContent> = new Map<number, InfoWindowMarkerContent>();

  mapLoading: boolean = true;

  constructor(private tools: SharedToolsService) { }

  ngAfterViewInit() {
    this.initialiseMap();
    // Custom scroll zoom logic
    const mapContainer = document.getElementById('map'); // Ensure this ID is correct
    if (mapContainer) {
      mapContainer.addEventListener('wheel', (event: WheelEvent) => this.tools.handleZoom(event, this.map));
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.mapOptions == null || this.map == null) {
      return;
    } else {
      this.clearMarkers();
      if (changes['locations'] && changes['locations'].currentValue?.length > 0) {
        this.recalculateMap();
      } else {
        if (this.focusedSimTower != null) {
          this.map.panTo({ lat: this.focusedSimTower.latitude, lng: this.focusedSimTower.longitude });
          this.map.setZoom(15);
          setTimeout(() => {
            this.mapLoading = false;
          });
          this.setMarkers();
        } else {
          setTimeout(() => {
            this.mapLoading = true;
          });
        }
      }
    }
  }

  recalculateMap() {
    // Sets default bounds over South Africa, works with zoom = 5.5
    if (this.calculateCenter == false) {
      this.mapBounds = new google.maps.LatLngBounds(
        {
          lat: -34.97116591092591,
          lng: 9.65280943749999
        },
        {
          lat: -21.81522929044154,
          lng: 38.17331724999999
        }
      );
    }

    this.setMarkers();

    this.mapOptions.center = this.mapBounds.getCenter();
    this.map.setOptions(this.mapOptions);

    if (this.clusterMarkers) {
      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
      });
    }

    //This is temporary, need to find a better way to get zoom after fitBounds and set it to an appropriate value
    if (this.focusedZoom == null && this.calculateCenter && this.markers.length <= 2 && !this.fitBounds) {
      this.map.setZoom(10);
    }

    if (this.focusedZoom != null) {
      this.map.setZoom(this.focusedZoom);
    }

    setTimeout(() => {
      this.mapLoading = false;
    });
  }

  initialiseMap() {
    this.mapOptions = {
      center: new google.maps.LatLng(this.lat, this.lng),
      zoom: this.focusedZoom ?? 5.3,
      scaleControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      mapTypeControl: false,
      minZoom: 4,
      zoomControl: true,
    };

    if (this.updateActionButtons) {
      this.mapOptions.zoomControlOptions = {
        position: google.maps.ControlPosition.TOP_RIGHT,
      }
    }

    // Sets default bounds over South Africa, works with zoom = 5.5
    if (this.calculateCenter == false) {
      this.mapBounds = new google.maps.LatLngBounds(
        {
          lat: -34.97116591092591,
          lng: 9.65280943749999
        },
        {
          lat: -21.81522929044154,
          lng: 38.17331724999999
        }
      );
    }

    if (this.gmap) {
      // this.setMarkers();
      this.mapOptions.center = this.mapBounds.getCenter();
      this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);

      // if (this.markers.length > 0 && this.clusterMarkers) {
      //   this.markerClusterer = new MarkerClusterer({
      //     map: this.map,
      //     markers: this.markers,
      //   });
      // }

      //This is temporary, need to find a better way to get zoom after fitBounds and set it to an appropriate value
      // if (this.locations != null && this.calculateCenter && this.markers.length <= 2) {
      //   this.map.setZoom(10);
      // }

      // this.addBoundsListener();

      if (this.locations != null) {

        this.recalculateMap();
      }
    }
  }

  setMarkers() {
    // this.clearMarkers();
    let tempMarkers: google.maps.Marker[] = [];
    if (this.calculateCenter == true) {
      this.mapBounds = new google.maps.LatLngBounds();
    }
    for (let i = 0; i < this.locations!.length; i++) {
      let point = this.locations![i];

      // Builds bounds as it goes through the locations, reduces O(2n) to O(n)
      if (this.calculateCenter == true) {
        if (point.latitude == null || point.longitude == null || (point.latitude == 0 && point.longitude == 0)) {
          continue;
        }
        this.mapBounds.extend(new google.maps.LatLng(point.latitude, point.longitude));
      }

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(point.latitude, point.longitude),
        map: this.map,
        icon: point.customIcon ? `${point.customIcon.icon}` : null,
      });

      this.markerMap.set(i, {
        marker: marker,
        location: point,
      });

      marker.addListener('click', () => {
        this.drawMarkerInfoWindow(i);
      });

      tempMarkers.push(marker);
    }
    this.markers = tempMarkers;
    if (this.fitBounds) {
      this.map.fitBounds(this.mapBounds);
    }
  }

  drawMarkerInfoWindow(markerIndex: number) {
    const point = this.markerMap.get(markerIndex)!.location;
    const marker = this.markerMap.get(markerIndex)!.marker;

    const infowindow = new google.maps.InfoWindow({
      content: point.infoWindowContent
    });

    infowindow.open(this.map, marker);

    infowindow.addListener('closeclick', () => {
      infowindow.close();
    });
  }

  clearMarkers(): void {
    this.markers.forEach(marker => {
      marker.setMap(null);
    });
    if (this.markerClusterer != null) {
      this.markerClusterer.clearMarkers();
    }
    this.markers = [];
  }

  buildBounds(data: BaseMapLocation[]): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    if (data != null) {
      data.forEach((element) => {
        if (element.latitude == null || element.longitude == null || (element.latitude == 0 && element.longitude == 0)) {
          return;
        }
        bounds.extend(new google.maps.LatLng(element.latitude, element.longitude));
      });
    }
    return bounds;
  }

  addBoundsListener() {
    this.map.addListener('bounds_changed', () => {
      this.debounce.next(true);
    });

    this.debounce$.pipe(debounceTime(500)).subscribe(() => {
      this.boundsChange.emit({
        SW: {
          Lat: this.map.getBounds()!.getSouthWest().lat(),
          Long: this.map.getBounds()!.getSouthWest().lng()
        },
        NE: {
          Lat: this.map.getBounds()!.getNorthEast().lat(),
          Long: this.map.getBounds()!.getNorthEast().lng(),
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.debounce.unsubscribe();
  }
}
